import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, catchError, of, tap } from 'rxjs';
import { AUTH_CONFIG, AuthResp } from './auth.config';
import { StorageService } from './storage.service';
import { TokenStore } from './token.store';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config = inject(AUTH_CONFIG);
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly tokens = inject(TokenStore);

  /** Fires after every successful refresh. juno drops its cached grantable roles on it. */
  readonly tokenRefreshed$ = new Subject<void>();
  /** Fires on logOut(). */
  readonly loggedOut$ = new Subject<void>();

  /** HTTP Basic sign-in. Resolves null on any failure; the caller shows "wrong credentials". */
  logIn<T extends AuthResp = AuthResp>(username: string, password: string): Observable<T | null> {
    const headers = new HttpHeaders().set('Authorization', `Basic ${btoa(`${username}:${password}`)}`);
    return this.http
      .post<T>(this.config.baseUrl + this.config.signInPath, {}, { headers })
      .pipe(catchError(() => of(null)));
  }

  /** Trades the refresh token for new tokens. TokenCachingInterceptor stores them. Null on failure or with no refresh token. */
  refreshToken<T extends AuthResp = AuthResp>(): Observable<T | null> {
    const refresh = this.tokens.refreshToken();
    if (!refresh) return of(null);
    const headers = new HttpHeaders().set('access_token', refresh.token);
    return this.http.post<T>(this.config.baseUrl + this.config.refreshPath, {}, { headers }).pipe(
      tap(() => this.tokenRefreshed$.next()),
      catchError(() => of(null)),
    );
  }

  logOut(): void {
    this.storage.clean();
    this.tokens.clear();
    this.loggedOut$.next();
  }
}
